// The snoozed tray at the end of the feed: when each card comes back, in plain local words, and
// bringing one back early. Pure, so tests pin the clock and the zone.
import type { TimeFormatOptions } from "@web/lib/time";

const DAY_MS = 86_400_000;

/** "Back today 4:00 PM", "Back tomorrow 9:00 AM", "Back Thu 9:00 AM", "Back Oct 20, 9:00 AM". */
export function backLabel(
  until: string | null,
  now: number | Date,
  options: TimeFormatOptions = {},
): string {
  const date = until ? new Date(until) : null;
  if (!date || Number.isNaN(date.getTime())) return "Snoozed";
  const nowDate = now instanceof Date ? now : new Date(now);
  if (date.getTime() <= nowDate.getTime()) return "Coming back now";
  const { locale, timeZone } = options;
  const format = (o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...o, timeZone }).format(date);
  const clock = format({ hour: "numeric", minute: "2-digit" });
  const days = localDayNumber(date, timeZone) - localDayNumber(nowDate, timeZone);
  if (days === 0) return `Back today ${clock}`;
  if (days === 1) return `Back tomorrow ${clock}`;
  if (days < 7) return `Back ${format({ weekday: "short" })} ${clock}`;
  const sameYear = format({ year: "numeric" }) === yearIn(nowDate, options);
  const day = format({ month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
  return `Back ${day}, ${clock}`;
}

export type SnoozeAgain = "snoozed" | "already_back";

/**
 * Brings a snoozed card back now and returns its undo: snooze it again until the very moment it
 * was due, unless that moment has passed meanwhile (then it would be back anyway, so it stays).
 */
export async function bringBack(
  card: { snoozedUntil: string | null },
  steps: {
    restore: () => Promise<unknown>;
    snoozeUntil: (until: string) => Promise<unknown>;
    now?: () => number;
  },
): Promise<() => Promise<SnoozeAgain>> {
  const until = card.snoozedUntil;
  await steps.restore();
  return async () => {
    const now = steps.now?.() ?? Date.now();
    if (!until || new Date(until).getTime() <= now) return "already_back";
    await steps.snoozeUntil(until);
    return "snoozed";
  };
}

/** Days since the epoch of `date`'s calendar day in `timeZone`. */
function localDayNumber(date: Date, timeZone: string | undefined): number {
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(date)
    .split("-")
    .map(Number);
  return Math.round(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / DAY_MS);
}

function yearIn(date: Date, options: TimeFormatOptions): string {
  return new Intl.DateTimeFormat(options.locale, {
    year: "numeric",
    timeZone: options.timeZone,
  }).format(date);
}
